import { gql, useApolloClient, useMutation, useQuery } from '@apollo/client';
import IconInfo from '@apollo/icons/small/IconInfo.svg';
import IconLock from '@apollo/icons/small/IconLock.svg';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import IconUnavailable from '@apollo/icons/small/IconUnavailable.svg';
import {
  Badge,
  Button,
  ButtonGroup,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Group,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import classnames from 'classnames';
import { uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Link as ReactRouterLink, useLocation } from 'react-router-dom';

import * as orgRoutes from 'src/app/account/routes';
import { InitiatingCloudEndpointBadge } from 'src/app/appLayout/components/globalHeaderGraphContent/initiatingCloudEndpointBadge/InitiatingCloudEndpointBadge';
import { LinkEndpointModalData } from 'src/app/graph/explorerPage/connectionSettingsModal/ConnectionSettingsModal';
import { GraphRef, graphRefToString } from 'src/app/graph/hooks/useGraphRef';
import {
  contractsConfigRoute,
  graphSettingsRouteConfig,
  variantSettingsRouteConfig,
} from 'src/app/graph/routes';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { Loading } from 'src/components/common/loading/Loading';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { SplitButton } from 'src/components/common/splitButton/SplitButton';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { ErrorMessage } from 'src/components/errorMessage/ErrorMessage';
import { PermissionBlock } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { RelativeTimestamp } from 'src/components/timestamp/Timestamp';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { useCheckPlanTypes } from 'src/hooks/useCheckPlanTypes';
import { useCurrentAccountId } from 'src/hooks/useCurrentAccountId';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { useTrackCustomEvent } from 'src/hooks/useTrackCustomEvent';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { GraphType } from 'src/lib/graphqlTypes/types';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { GRAPH_VARIANT_PLACEHOLDER } from 'src/lib/roverCommands';
import { scrollToIfNotInView } from 'src/lib/scrollTo';

import { RoverAddSubgraphModal } from '../../subgraphsLayout/subgraphsPage/roverAddSubgraphModal/RoverAddSubgraphModal';
import { getMatchingAllowedTrack } from '../../variantSettingsPage/variantSettingsCards/FederationVersionInfo';

import { AddVariantWizard } from './addVariantWizard/AddVariantWizard';
import { RoverAddVariantModal } from './roverAddVariantModal/RoverAddVariantModal';

export const GraphVariantsSettingsPageQuery = gql`
  query GraphSettings__VariantsPage($graphId: ID!) {
    graph: service(id: $graphId) {
      id
      title
      registrySubscriptionsEnabled
      graphType
      roles {
        canQuerySchemas
      }
      variants {
        id
        url
        sendCookies
        subscriptionUrl
        name
        compositionVersion
        supportedDirectives {
          name
          enabled
        }
        sourceVariant {
          id
          name
          compositionVersion
          supportedDirectives {
            name
            enabled
          }
          contractFilterConfig {
            include
            exclude
          }
          activeSchemaPublish {
            id
            schema {
              createdAt
              hash
              typeCount
            }
          }
        }
        isContract
        isProtected
        isPublic
        hasManagedSubgraphs
        derivedVariantCount
        activeSchemaPublish {
          id
          publishedAt
          schema {
            createdAt
            hash
            typeCount
          }
        }
        allowedTracks {
          badge
          buildPipelineTrack
          compositionVersion
          deprecatedAt
          displayName
          minimumGatewayVersion
          minimumRouterVersion
          notSupportedAt
        }
        buildConfig {
          buildPipelineTrack
        }
      }
      roles {
        canManageVariants
      }
      graphCapabilities {
        minimumBuildPipelineTrack
      }
    }
  }
`;

export const DeleteVariantMutation = gql<
  GraphQLTypes.VariantSettings__DeleteVariantMutation,
  GraphQLTypes.VariantSettings__DeleteVariantMutationVariables
>`
  mutation VariantSettings__DeleteVariantMutation(
    $serviceId: ID!
    $schemaTag: String!
  ) {
    service(id: $serviceId) {
      service {
        id
      }
      deleteSchemaTag(tag: $schemaTag) {
        deleted
      }
    }
  }
`;

export const SourceVariantsPage = ({ graphRef }: { graphRef: GraphRef }) => {
  const { data, loading, error } = useQuery<
    GraphQLTypes.GraphSettings__VariantsPage,
    GraphQLTypes.GraphSettings__VariantsPageVariables
  >(GraphVariantsSettingsPageQuery, {
    variables: {
      graphId: graphRef.graphId,
    },
  });

  const trackCustomEvent = useTrackCustomEvent();
  const location = useLocation();
  const [currentAccountId] = useCurrentAccountId();
  const currentPlan = useCurrentPlan({
    skip: !currentAccountId,
    accountId: currentAccountId as string,
  });
  const { isDedicatedPlan } = useCheckPlanTypes({
    tier: currentPlan?.currentPlan?.tier,
    kind: currentPlan?.currentPlan?.kind,
  });
  const isCloudSupergraph =
    data?.graph?.graphType === GraphType.CLOUD_SUPERGRAPH;

  const [shouldShowHighlight, setShouldShowHighlight] = useState(false);
  const { highlightVariantState } = useRouteParams(graphSettingsRouteConfig);

  const sourceVariants = React.useMemo(
    () => data?.graph?.variants.filter((v) => !v.isContract) ?? [],
    [data],
  );
  const allVariants = React.useMemo(
    () => data?.graph?.variants.map(({ name }) => name) ?? [],
    [data],
  );

  useEffect(() => {
    if (
      sourceVariants.some(
        (variant) => highlightVariantState?.variant === variant.name,
      )
    ) {
      setShouldShowHighlight(true);
      scrollToIfNotInView(`#${highlightVariantState?.variant}`);
      const timeout = setTimeout(() => {
        setShouldShowHighlight(false);
      }, 5_000);
      return () => clearTimeout(timeout);
    }
  }, [highlightVariantState?.variant, sourceVariants]);

  if (loading || currentPlan.loading) return <Loading />;
  if (error || currentPlan.error)
    return (
      <ErrorMessage className="mb-10" error={error ?? currentPlan.error} />
    );
  if (!data?.graph || !currentPlan.currentPlan) return <NotFound />;

  const contractVariants = data?.graph?.variants.filter((v) => v.isContract);
  const sourceVariantsWithContracts = uniq(
    data?.graph?.variants.map((v) => v.sourceVariant?.name),
  ).filter((x) => !!x).length;

  const contractsEnabled = !!currentPlan?.currentPlan?.contracts;

  const onboardNewGraphVariant = isDedicatedPlan
    ? orgRoutes.supergraphOnboarding.path({
        orgId: currentAccountId ?? '',
        prefillGraphRef: graphRefToString(graphRef),
        includeVariantOnboardingStep: 'true',
      })
    : setOverlayVisibleLocation({
        location,
        overlayName: Config.modals.addVariant,
      });

  const hasManagedSubgraphs = sourceVariants.some((v) => v.hasManagedSubgraphs);

  return (
    <>
      <LazyUrlModal name={Config.modals.roverAddSubgraph}>
        {({ closeModal, isOpen }) => (
          <RoverAddSubgraphModal
            withNewVariant={true}
            isOpen={isOpen}
            onClose={closeModal}
            graphRef={{
              graphId: graphRef.graphId,
              graphVariant: GRAPH_VARIANT_PLACEHOLDER,
            }}
            title={`Add a variant to ${data?.graph?.title}`}
            description="Follow the steps below to publish a subgraph that you have running in the cloud of your choice to a new variant:"
          />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.roverAddVariant}>
        {({ closeModal, isOpen }) => (
          <RoverAddVariantModal
            isOpen={isOpen}
            onClose={closeModal}
            graphId={graphRef.graphId}
            title={`Add a variant to ${data?.graph?.title}`}
            description="Follow the steps below to publish a variant."
          />
        )}
      </LazyUrlModal>
      <LazyUrlModal name={Config.modals.addVariant}>
        {({ closeModal, isOpen }) => (
          <AddVariantWizard
            graphRef={graphRef}
            onClose={closeModal}
            isOpen={isOpen}
            allVariants={allVariants}
            isCloudSupergraph={isCloudSupergraph}
            hasManagedSubgraphs={
              !!data.graph?.variants.some((v) => v.hasManagedSubgraphs)
            }
          />
        )}
      </LazyUrlModal>
      <Card>
        <CardHeader>
          <CardTitle>
            Variants <Badge>{sourceVariants.length}</Badge>
          </CardTitle>
          <CardDescription>
            Your graph has {sourceVariants.length}{' '}
            {format.pluralize(sourceVariants.length, 'registry variant')} and{' '}
            {contractVariants.length === 0
              ? 'no contracts'
              : `${contractVariants.length} ${format.pluralize(
                  contractVariants.length,
                  'contract',
                )} derived from ${sourceVariantsWithContracts} of those ${format.pluralize(
                  sourceVariantsWithContracts,
                  'variant',
                )}`}
            . They are kept up-to-date by publishing with{' '}
            <Link isExternal to="https://www.apollographql.com/docs/rover/">
              Rover
            </Link>{' '}
            or using{' '}
            <Link
              isExternal
              to="https://www.apollographql.com/docs/apollo-server/api/plugin/schema-reporting"
            >
              automatic schema reporting
            </Link>{' '}
            in Apollo Server.
          </CardDescription>
          <SplitButton
            ariaLabel="Add variant dropdown"
            variant="primary"
            as={ReactRouterLink}
            to={onboardNewGraphVariant}
            onClick={() => {
              trackCustomEvent({
                action: 'click_add_variant',
                category: 'Graph Settings Variants',
                label: `graphRef:${graphRefToString(graphRef)}`,
              });
            }}
            menuItems={
              // hide split button for dedicated until supported in rover
              isDedicatedPlan
                ? undefined
                : [
                    <MenuItem
                      key="add-variant-from-rover"
                      as={ReactRouterLink}
                      to={setOverlayVisibleLocation({
                        location,
                        overlayName: data.graph?.variants.some(
                          (v) => v.hasManagedSubgraphs,
                        )
                          ? Config.modals.roverAddSubgraph
                          : Config.modals.roverAddVariant,
                      })}
                      onClick={() => {
                        trackCustomEvent({
                          action: 'click_rover_add_variant',
                          category: 'Graph Settings Variants',
                          label: `graphRef:${graphRefToString(graphRef)}`,
                        });
                      }}
                    >
                      Add variant using the Rover CLI
                    </MenuItem>,
                  ]
            }
          >
            Add a variant
          </SplitButton>
        </CardHeader>
        <CardBody>
          {!data.graph.roles?.canQuerySchemas ? (
            <PermissionBlock />
          ) : sourceVariants.length === 0 ? (
            'No variants reported via Schema Registry'
          ) : (
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th className="w-[30%]">Name</Th>
                    <Th className="w-[10%]">Contracts</Th>
                    <Th isNumeric className="w-[5%]">
                      Types
                    </Th>
                    <Th className="w-[5%] whitespace-nowrap">Last published</Th>
                    {hasManagedSubgraphs && (
                      <Th className="w-[10%] whitespace-nowrap">
                        <Group>
                          Federation version
                          <Tooltip label="The version of Apollo Federation to use when building this variant.">
                            <IconInfo className="text-icon-primary" />
                          </Tooltip>
                        </Group>
                      </Th>
                    )}
                    <Th className="whitespace-nowrap">Supported directives</Th>
                    <Th>
                      <span className="sr-only">Actions</span>
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {sourceVariants.map((variant) => {
                    const {
                      allowedTracks,
                      activeSchemaPublish,
                      buildConfig,
                      derivedVariantCount,
                      name,
                      supportedDirectives,
                    } = variant;
                    const { isDisabled, disabledReason } = isContractDisabled(
                      contractsEnabled,
                      supportedDirectives || [],
                    );
                    const matchingTrack = getMatchingAllowedTrack(
                      allowedTracks.filter(
                        (t): t is NonNullable<typeof t> => !!t,
                      ),
                      buildConfig?.buildPipelineTrack,
                    );

                    return (
                      <Tr key={variant.id}>
                        <Td>
                          <Group gap={2}>
                            <Link
                              id={variant.name}
                              to={variantSettingsRouteConfig.path({
                                graphId: graphRef.graphId,
                                graphVariant: variant.name,
                                tab: 'general',
                              })}
                              className={classnames(
                                highlightVariantState?.variant ===
                                  variant.name &&
                                  shouldShowHighlight &&
                                  'bg-searchhighlight text-inverted',
                              )}
                            >
                              {variant.name}
                              {variant.name === 'current' ? ' (default)' : ''}
                            </Link>
                            <IsCloudEndpointInitiatingContextProvider
                              graphRef={{
                                graphId: graphRef.graphId,
                                graphVariant: variant.name,
                              }}
                            >
                              <InitiatingCloudEndpointBadge />
                            </IsCloudEndpointInitiatingContextProvider>

                            {data.graph?.graphType ===
                              GraphQLTypes.GraphType.CLASSIC &&
                              variant.hasManagedSubgraphs && (
                                <Badge>Federated</Badge>
                              )}
                            {variant.isPublic && <Badge>Public</Badge>}
                            {variant.isProtected && (
                              <Badge variant="warn" icon={<IconLock />}>
                                Protected
                              </Badge>
                            )}
                          </Group>
                        </Td>
                        <Td>
                          {isDisabled ? (
                            <Tooltip label={disabledReason}>
                              <IconUnavailable className="text-icon-disabled" />
                            </Tooltip>
                          ) : (
                            <Link
                              to={variantSettingsRouteConfig.path({
                                graphId: graphRef.graphId,
                                graphVariant: name,
                                tab: 'contracts',
                              })}
                            >
                              {derivedVariantCount.toLocaleString()}{' '}
                              {format.pluralize(
                                derivedVariantCount,
                                'contract',
                              )}
                            </Link>
                          )}
                        </Td>
                        <Td isNumeric>
                          {activeSchemaPublish?.schema.typeCount.toLocaleString()}
                        </Td>
                        <Td>
                          {activeSchemaPublish && (
                            <RelativeTimestamp
                              value={activeSchemaPublish.publishedAt}
                            />
                          )}
                        </Td>
                        {hasManagedSubgraphs && (
                          <Td>
                            {matchingTrack
                              ? matchingTrack.displayName
                              : 'Unknown'}
                          </Td>
                        )}
                        <Td>
                          <Group gap={2}>
                            {supportedDirectives
                              ?.filter(({ enabled }) => enabled)
                              .map((directive) => (
                                <Badge key={directive.name}>
                                  @{directive.name}
                                </Badge>
                              ))}
                          </Group>
                        </Td>
                        <Td isActionsMenu>
                          <VariantKebabMenu
                            canManageVariants={
                              data.graph?.roles?.canManageVariants
                            }
                            contractsDisabled={isDisabled}
                            graphId={graphRef.graphId}
                            variantName={variant.name}
                            contractsDisabledReason={disabledReason}
                            derivedVariantCount={variant.derivedVariantCount}
                            registrySubscriptionsEnabled={
                              data.graph?.registrySubscriptionsEnabled
                            }
                          />
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          )}
        </CardBody>
      </Card>
    </>
  );
};

export const isContractDisabled = (
  contractsEnabled: boolean,
  supportedDirectives: { name: string; enabled: boolean }[],
) => {
  return {
    isDisabled: !(
      contractsEnabled &&
      !!supportedDirectives?.find((d) => d.name === 'tag' && d.enabled)
    ),
    disabledReason: !contractsEnabled ? (
      <div
        data-analytics-category="Contracts"
        data-analytics-label="Settings: CTA: non-enterprise"
      >
        This is an Enterprise feature
      </div>
    ) : !supportedDirectives?.find((d) => d.name === 'tag' && d.enabled) ? (
      <div
        data-analytics-category="Contracts"
        data-analytics-label="Settings: CTA: tag-not-supported"
      >
        This variant must support the{' '}
        <span className="font-mono text-sm">@tag</span> directive before you can
        create a contract from it
      </div>
    ) : undefined,
  };
};

// XXX Contributors can edit endpoint URLs for non-protected variants but they
// can't delete them. We are currently choosing to hide both options on this
// page from them (but still allow contributors to edit the endpoint URL inside
// Studio itself). We could make this more precise.
const VariantKebabMenu = ({
  canManageVariants,
  graphId,
  variantName,
  contractsDisabled,
  contractsDisabledReason,
  derivedVariantCount,
  registrySubscriptionsEnabled,
}: {
  canManageVariants?: boolean;
  graphId: string;
  variantName: string;
  contractsDisabled: boolean;
  contractsDisabledReason?: React.ReactNode;
  derivedVariantCount: number;
  registrySubscriptionsEnabled?: boolean;
}) => {
  const location = useLocation<{
    linkEndpointModalData: LinkEndpointModalData;
  }>();
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const client = useApolloClient();

  const [deleteTag, { loading, error }] = useMutation(DeleteVariantMutation, {
    variables: { serviceId: graphId, schemaTag: variantName },
    onCompleted: () => {
      client.cache.evict({
        id: client.cache.identify({
          __typename: 'GraphVariant',
          id: graphRefToString({ graphId, graphVariant: variantName }),
        }),
      });
      setDeleteModalOpen(false);
    },
  });

  return (
    <>
      <Menu placement="bottom-end" autoSelect={false}>
        <Tooltip
          disabled={canManageVariants}
          label="You do not have permission to manage variants"
          shouldWrapChildren={!canManageVariants}
        >
          <MenuButton
            as={IconButton}
            isDisabled={!canManageVariants}
            variant="secondary"
          >
            <IconOverflow />
          </MenuButton>
        </Tooltip>
        <MenuContent>
          <MenuList>
            <Tooltip
              disabled={!contractsDisabled}
              label={contractsDisabledReason}
              shouldWrapChildren
              placement="top"
            >
              <MenuItem
                isDisabled={contractsDisabled}
                as={ReactRouterLink}
                to={contractsConfigRoute.locationFrom({
                  location,
                  patch: {
                    sourceVariant: variantName,
                    overlay: Config.modals.configureContractWizard,
                  },
                  fromRouteConfig: graphSettingsRouteConfig,
                })}
              >
                Create contract from variant
              </MenuItem>
            </Tooltip>

            <MenuItem
              as={ReactRouterLink}
              to={variantSettingsRouteConfig.path({
                graphId,
                graphVariant: variantName,
                tab: 'general',
              })}
            >
              Edit variant
            </MenuItem>

            <Tooltip
              disabled={derivedVariantCount <= 0}
              label="You cannot delete this variant while it still has derived contract variants"
              shouldWrapChildren
            >
              <MenuItem
                isDisabled={derivedVariantCount > 0}
                onClick={() =>
                  derivedVariantCount === 0 && setDeleteModalOpen(true)
                }
              >
                Delete variant
              </MenuItem>
            </Tooltip>
          </MenuList>
        </MenuContent>
      </Menu>

      {deleteModalOpen && (
        <Modal isOpen onClose={() => setDeleteModalOpen(false)} size="lg">
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <ModalTitle>
                Are you sure you want to delete{' '}
                {(
                  variantName + (variantName === 'current' ? ' (default)' : '')
                ).toUpperCase()}
                ?
              </ModalTitle>
              {registrySubscriptionsEnabled && (
                <ModalDescription>
                  Deleting your variant will also remove all your related
                  configurations including schema update notification(s).
                </ModalDescription>
              )}
            </ModalHeader>
            <ModalBody>
              <em>This action cannot be undone.</em>
              {!!error && <ErrorMessage error={error} />}
            </ModalBody>
            <ModalFooter>
              <ButtonGroup>
                <Button
                  variant="secondary"
                  onClick={() => setDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  isLoading={loading}
                  loadingText="Yes, Delete"
                  onClick={() => deleteTag()}
                >
                  Yes, Delete
                </Button>
              </ButtonGroup>
            </ModalFooter>
          </ModalContent>
        </Modal>
      )}
    </>
  );
};
